import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AppModal } from "global/components/AppModal";
import { CustomInput } from "global/components";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import strings from "global/constants/StringConstants";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import { capitalizeText, isTruthy } from "helpers/methods";
import { CustomField, FieldListItem, FileField } from "models/interfaces";
import { focusRing, headingText } from "theme/colors";
import { useAppSelector } from "redux/hooks";
import { selectDefaultDateFormat } from "redux/preferencesSlice";
import {
  FIELD_LENGTH_LIMIT_MESSAGE,
  LIBRARY_FIELD_TYPES,
  MAX_FIELD_LENGTH,
  blockNonLengthKeys,
  toLengthDigits,
} from "../SignRequestHelper";
import { PlusIcon, getFieldTypeIcon } from "./EditorIcons";

/** Select value that switches the pattern list to a sender-typed regex. */
const CUSTOM_PATTERN_VALUE = "__custom__";

/** Used when a custom pattern is saved without a message of its own. */
const DEFAULT_VALIDATION_MESSAGE = "Value is not valid.";

/**
 * Character caps for the free-text settings. The server stores these without a limit of its
 * own, so these are the only guard against a pasted essay landing in a tooltip.
 */
const TEXT_LIMITS = {
  name: 100,
  description: 255,
  label: 100,
  placeholder: 100,
  toolTip: 255,
  option: 100,
  pattern: 500,
  validationMessage: 255,
} as const;

/**
 * A length input's value as a whole non-negative number. Deliberately NOT clamped to
 * MAX_FIELD_LENGTH: silently rewriting 5000 to 4000 leaves the sender staring at a number they
 * did not type with nothing to explain it. The value is kept as typed and refused with a message
 * instead, and the save is blocked, so an out-of-range number never reaches the server.
 */
const lengthInput = (event: any): number =>
  Number(toLengthDigits(event.target.value)) || 0;

type ErrorKey =
  | "name"
  | "description"
  | "pattern"
  | "validationMessage"
  | "maxLength"
  | "minLength"
  | "options"
  | "defaultValue"
  | "readOnly";

/**
 * Which inputs each error depends on. An error shows once any of its inputs has been touched
 * (or on save) — so lowering Max Length below the default flags the default straight away,
 * without waiting for the sender to revisit that field.
 */
const ERROR_SOURCES: Record<ErrorKey, (keyof FormState)[]> = {
  name: ["name"],
  description: ["description"],
  pattern: ["validationPattern"],
  validationMessage: ["validationMessage"],
  maxLength: ["maxLength"],
  minLength: ["minLength", "maxLength"],
  options: ["listItems"],
  defaultValue: [
    "defaultValue",
    "minLength",
    "maxLength",
    "validationPattern",
    "listItems",
  ],
  readOnly: ["required", "readOnly", "defaultValue"],
};

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];

const selectSx = {
  fontSize: "0.875rem",
  "& .MuiSelect-select:focus-visible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "-2px",
  },
};

const captionSx = { color: "text.secondary", mb: 0.5, display: "block" };
const helperSx = { color: "text.secondary", display: "block", mt: 0.5 };
const sectionTitleSx = {
  fontWeight: 600,
  color: "text.primary",
  display: "block",
};

const typeLabel = (type: string) =>
  type === strings.radioButton
    ? "Radio Button"
    : type === strings.dropDown
      ? "Drop Down"
      : capitalizeText(type);

/** Caption above an input; `required` adds the asterisk the form expects. */
const Caption = ({
  children,
  required,
  id,
}: {
  children: React.ReactNode;
  required?: boolean;
  id?: string;
}) => (
  <Typography id={id} variant="caption" sx={captionSx}>
    {children}
    {required && (
      <Box component="span" aria-hidden="true" sx={{ color: "error.main", ml: 0.25 }}>
        *
      </Box>
    )}
  </Typography>
);

/** Everything the modal edits, flattened so each input binds to one key. */
interface FormState {
  name: string;
  description: string;
  fieldType: string;
  label: string;
  placeholder: string;
  toolTip: string;
  required: boolean;
  readOnly: boolean;
  multiLine: boolean;
  customValidation: boolean;
  validationPattern: string;
  validationMessage: string;
  minLength: number;
  maxLength: number;
  defaultValue: string;
  dateFormat: string;
  listItems: FieldListItem[];
}

const emptyForm = (defaultDateFormat: string): FormState => ({
  name: "",
  description: "",
  fieldType: strings.text,
  label: "",
  placeholder: "",
  toolTip: "",
  required: true,
  readOnly: false,
  multiLine: false,
  customValidation: false,
  validationPattern: "none",
  validationMessage: "",
  minLength: 0,
  maxLength: 200,
  defaultValue: "",
  dateFormat: defaultDateFormat,
  listItems: [{ id: "option_1", text: "Option 1" }],
});

const fromCustomField = (
  customField: CustomField,
  defaultDateFormat: string,
): FormState => {
  const config = customField.config ?? {};
  const base = emptyForm(defaultDateFormat);
  return {
    ...base,
    name: customField.name ?? "",
    description: customField.description ?? "",
    fieldType: customField.fieldType ?? strings.text,
    label: config.label ?? "",
    placeholder: config.placeholder ?? "",
    toolTip: config.toolTip ?? "",
    required: config.required ?? true,
    readOnly: config.readOnly ?? false,
    multiLine: config.multiLine ?? false,
    customValidation: config.customValidation ?? false,
    validationPattern: config.validationPattern || "none",
    validationMessage: config.validationMessage ?? "",
    minLength: config.minLength ?? 0,
    maxLength: config.maxLength || 200,
    defaultValue: config.defaultValue ?? "",
    dateFormat: config.dateFormat || defaultDateFormat,
    listItems: config.listItems?.length ? config.listItems : base.listItems,
  };
};

/** The definition as the server stores it: the form folded back into a FileField config. */
const toCustomField = (form: FormState, id?: string): CustomField => {
  const isText = form.fieldType === strings.text;
  const isDropDown = form.fieldType === strings.dropDown;
  const isDate = form.fieldType === strings.date;
  const hasRule =
    isText && (form.customValidation || form.validationPattern !== "none");
  const config: Partial<FileField> = {
    label: form.label.trim() || form.name.trim(),
    placeholder: form.placeholder,
    toolTip: form.toolTip,
    required: form.required,
    readOnly: form.readOnly,
    multiLine: isText ? form.multiLine : false,
    // A definition is never assigned to "any signer"; the signer is chosen when the
    // field is placed, and the placement's own signerIndex overrides this anyway.
    signerIndex: 0,
    customValidation: isText ? form.customValidation : false,
    validationPattern: isText ? form.validationPattern || "none" : "none",
    // A custom pattern's message is what the signer is shown when the value is
    // refused, so it never goes out empty.
    validationMessage: hasRule
      ? form.validationMessage.trim() || DEFAULT_VALIDATION_MESSAGE
      : "",
    minLength: isText ? form.minLength : 0,
    // A dropdown has no Max Length of its own: its value is always one of the options, so the
    // stored limit is just the longest of them. Nothing enforces it, but it keeps the saved
    // config honest rather than carrying an arbitrary 200.
    maxLength: isText
      ? form.maxLength || 200
      : isDropDown
        ? Math.max(
            1,
            ...form.listItems.map((item) => item.text.trim().length),
          )
        : 200,
    defaultValue: isText || isDropDown ? form.defaultValue : "",
    dateFormat: isDate ? form.dateFormat : undefined,
    listItems: isDropDown
      ? form.listItems.map((item) => ({ ...item, text: item.text.trim() }))
      : [],
    styles: {
      fontFamily: "Times",
      color: headingText,
      size: 16,
      fontWeight: { bold: false, italic: false, underline: false },
    },
  };
  return {
    id,
    name: form.name.trim(),
    description: form.description.trim(),
    fieldType: form.fieldType,
    config,
  };
};

interface CustomFieldModalProps {
  open: boolean;
  /** Present when editing; absent when creating. */
  customField?: CustomField | null;
  existingNames: string[];
  onClose: () => void;
  onSave: (customField: CustomField) => Promise<boolean>;
}

/**
 * Create / edit one definition in the custom field library. Same settings as
 * the Properties panel's Field Data and Validations sections, so what is saved
 * here is exactly what a placed field starts with. Inline checks mirror the
 * server's validateFieldConfiguration; the server remains the final word.
 */
const CustomFieldModal: React.FC<CustomFieldModalProps> = ({
  open,
  customField,
  existingNames,
  onClose,
  onSave,
}) => {
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);
  const [form, setForm] = useState<FormState>(() =>
    emptyForm(defaultDateFormat),
  );
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Set<keyof FormState>>(() => new Set());
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setSaving(false);
    setTouched(new Set());
    setForm(
      customField
        ? fromCustomField(customField, defaultDateFormat)
        : emptyForm(defaultDateFormat),
    );
  }, [open, customField, defaultDateFormat]);

  /** Marks an input as visited, which lets the errors that depend on it show. */
  const touch = (key: keyof FormState) =>
    setTouched((current) =>
      current.has(key) ? current : new Set(current).add(key),
    );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  /** For controls with no "typing" phase (switches, selects, numbers): touched on change. */
  const setAndTouch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    set(key, value);
    touch(key);
  };

  const isText = form.fieldType === strings.text;
  const isDropDown = form.fieldType === strings.dropDown;
  const isDate = form.fieldType === strings.date;
  const isCheckbox = form.fieldType === strings.checkbox;
  const isRadio = form.fieldType === strings.radioButton;
  // Only a typed value has a length worth limiting. A dropdown's value is one of its options, and
  // checkbox / radio carry true/false.
  const hasLengthLimits = isText;

  // ---- validation ---------------------------------------------------------

  const errors = useMemo(() => {
    const result: Partial<Record<ErrorKey, string>> = {};
    const name = form.name.trim();
    if (!name) result.name = "Enter a name for this field.";
    else if (name.length > TEXT_LIMITS.name)
      result.name = `The name can be at most ${TEXT_LIMITS.name} characters.`;
    else if (
      existingNames.some(
        (existing) =>
          existing.toLowerCase() === name.toLowerCase() &&
          existing.toLowerCase() !== (customField?.name ?? "").toLowerCase(),
      )
    )
      result.name = "You already have a custom field with this name.";
    if (form.description.length > TEXT_LIMITS.description)
      result.description = `The description can be at most ${TEXT_LIMITS.description} characters.`;

    if (isText && form.customValidation) {
      const pattern = form.validationPattern.trim();
      if (!pattern) result.pattern = "Enter a pattern.";
      else {
        try {
          new RegExp(pattern);
        } catch {
          result.pattern = "This pattern is not valid.";
        }
      }
      if (!form.validationMessage.trim())
        result.validationMessage =
          "Tell the signer what to enter when the value does not match.";
    }
    // Kept short: each sits under a half-width input.
    if (hasLengthLimits) {
      if (form.maxLength <= 0) result.maxLength = "Enter at least 1.";
      else if (form.maxLength > MAX_FIELD_LENGTH)
        result.maxLength = FIELD_LENGTH_LIMIT_MESSAGE;
      if (form.minLength > MAX_FIELD_LENGTH)
        result.minLength = FIELD_LENGTH_LIMIT_MESSAGE;
      else if (form.maxLength > 0 && form.minLength > form.maxLength)
        result.minLength = "Can't be more than Max Length.";
    }
    if (isDropDown) {
      const texts = form.listItems.map((item) => item.text.trim());
      if (texts.some((text) => text === ""))
        result.options = "Give every option a label.";
      else if (new Set(texts).size !== texts.length)
        result.options = "Each option must be different.";
      if (form.defaultValue && !texts.includes(form.defaultValue.trim()))
        result.defaultValue = "Pick a default from the options above.";
    }
    // Required + Read Only with nothing to satisfy it leaves the signer unable to finish the
    // document, so the server refuses it on send. Say so here instead.
    const canHaveDefault = isText || isDropDown;
    if (form.required && form.readOnly && !form.defaultValue.trim())
      result.readOnly = canHaveDefault
        ? "A required field the signer cannot edit needs a default value. Add one, or turn off Required or Read Only."
        : "The signer cannot fill a field that is both Required and Read Only. Turn one of them off.";

    // Trimmed, as the server checks (and applies) it.
    if (isText && form.defaultValue.trim()) {
      const value = form.defaultValue.trim();
      if (value.length > form.maxLength)
        result.defaultValue = "The default is longer than the maximum length.";
      else if (form.minLength > 0 && value.length < form.minLength)
        result.defaultValue = "The default is shorter than the minimum length.";
      else if (form.validationPattern !== "none") {
        let pattern: RegExp | null = null;
        try {
          pattern = form.customValidation
            ? new RegExp(form.validationPattern)
            : (signerFieldValidationPatterns[
                form.validationPattern as keyof typeof signerFieldValidationPatterns
              ] ?? null);
        } catch {
          pattern = null;
        }
        if (pattern && !pattern.test(value))
          result.defaultValue = "The default does not match the validation rule.";
      }
    }
    return result;
  }, [form, existingNames, customField, isText, isDropDown, hasLengthLimits]);

  const hasErrors = Object.keys(errors).length > 0;
  /** An error shows once one of its inputs has been touched, or after a save attempt. */
  const showError = (key: ErrorKey) =>
    submitted || ERROR_SOURCES[key].some((source) => touched.has(source))
      ? errors[key]
      : undefined;

  /**
   * The form scrolls inside the modal, so the first problem can sit out of view below the fold.
   * Bring it into view and put focus on it (or on its input, when it has one).
   */
  const revealFirstError = () =>
    requestAnimationFrame(() => {
      const first = formRef.current?.querySelector<HTMLElement>(
        ".Mui-error, [data-field-error]",
      );
      if (!first) return;
      first.scrollIntoView({ block: "center", behavior: "smooth" });
      first
        .closest("[data-field]")
        ?.querySelector<HTMLElement>("input, textarea, [role='button']")
        ?.focus({ preventScroll: true });
    });

  const handleSave = async () => {
    setSubmitted(true);
    if (hasErrors) {
      revealFirstError();
      return;
    }
    setSaving(true);
    const saved = await onSave(toCustomField(form, customField?.id));
    setSaving(false);
    if (saved) onClose();
  };

  // ---- dropdown options ---------------------------------------------------

  const addOption = () =>
    set("listItems", [
      ...form.listItems,
      {
        id: `option_${Date.now()}`,
        text: `Option ${form.listItems.length + 1}`,
      },
    ]);
  const renameOption = (index: number, text: string) =>
    set(
      "listItems",
      form.listItems.map((item, i) => (i === index ? { ...item, text } : item)),
    );
  const removeOption = (index: number) => {
    const next = form.listItems.filter((_, i) => i !== index);
    set("listItems", next);
    if (!next.some((item) => item.text.trim() === form.defaultValue)) {
      set("defaultValue", "");
    }
  };

  // ---- render -------------------------------------------------------------

  const switchRow = (
    label: string,
    key: "required" | "readOnly" | "multiLine",
    hint: string,
  ) => {
    // Only Read Only carries an error of its own (Required + Read Only with no default).
    const error = key === "readOnly" ? showError("readOnly") : undefined;
    return (
      <Box data-field>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ pr: 2 }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {hint}
            </Typography>
          </Box>
          <CustomSwitch
            name={key}
            aria-label={label}
            checked={form[key]}
            onChange={(e: any) => setAndTouch(key, e.target.checked)}
          />
        </Stack>
        {error && (
          <Typography
            variant="caption"
            role="alert"
            data-field-error
            sx={{ color: "error.main", display: "block", mt: 0.5 }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={saving ? undefined : onClose}
      title={customField ? "Edit Custom Field" : "Create Custom Field"}
      subtitle="Set it up once. It will appear under Custom Fields, ready to drag onto any document."
      size="md"
      scrollBody
      busy={saving}
      primaryLabel={customField ? "Save Changes" : "Create Field"}
      onPrimary={handleSave}
      primaryLoading={saving}
      cancelLabel="Cancel"
    >
      <Stack spacing={2} ref={formRef}>
        <Typography variant="caption" sx={sectionTitleSx}>
          Basics
        </Typography>

        <Box data-field>
          <Caption required>Name</Caption>
          <CustomInput
            size="small"
            id="custom-field-name"
            value={form.name}
            onChange={(e: any) => set("name", e.target.value)}
            onBlur={() => touch("name")}
            placeHolder="Enter field name"
            error={showError("name")}
            helperText={showError("name")}
            propsToInputElement={{
              "aria-label": "Custom field name",
              maxLength: TEXT_LIMITS.name,
            }}
          />
          {!showError("name") && (
            <Typography variant="caption" sx={helperSx}>
              How this field is listed under Custom Fields.
            </Typography>
          )}
        </Box>

        <Box data-field>
          <Caption>Description</Caption>
          <CustomInput
            size="small"
            id="custom-field-description"
            value={form.description}
            onChange={(e: any) => set("description", e.target.value)}
            onBlur={() => touch("description")}
            placeHolder="Enter description"
            error={showError("description")}
            helperText={showError("description")}
            propsToInputElement={{
              "aria-label": "Description",
              maxLength: TEXT_LIMITS.description,
            }}
          />
          {!showError("description") && (
            <Typography variant="caption" sx={helperSx}>
              Only you and your team see this.
            </Typography>
          )}
        </Box>

        <Box>
          <Caption id="custom-field-type-label">Type</Caption>
          <Select
            fullWidth
            size="small"
            labelId="custom-field-type-label"
            value={form.fieldType}
            onChange={(e) => set("fieldType", e.target.value as string)}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
            renderValue={(value) => {
              const Glyph = getFieldTypeIcon(value as string);
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Glyph size={16} />
                  <span>{typeLabel(value as string)}</span>
                </Stack>
              );
            }}
          >
            {LIBRARY_FIELD_TYPES.map((type) => {
              const Glyph = getFieldTypeIcon(type);
              return (
                <MenuItem key={type} value={type} sx={{ fontSize: "0.875rem" }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Glyph size={16} />
                    <span>{typeLabel(type)}</span>
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
          {isRadio && (
            <Typography variant="caption" sx={helperSx}>
              Each time you drag this in, one option is added. Group them
              together in the Properties panel.
            </Typography>
          )}
        </Box>

        <Divider />

        <Typography variant="caption" sx={sectionTitleSx}>
          Field Data
        </Typography>

        <Box>
          <Caption>Label</Caption>
          <CustomInput
            size="small"
            id="custom-field-label"
            value={form.label}
            onChange={(e: any) => set("label", e.target.value)}
            placeHolder="Enter field label"
            propsToInputElement={{
              "aria-label": "Field label",
              maxLength: TEXT_LIMITS.label,
            }}
          />
          <Typography variant="caption" sx={helperSx}>
            Shown on the document. Leave it empty to use the name.
          </Typography>
        </Box>

        {!isCheckbox && !isRadio && (
          <Box>
            <Caption>Placeholder</Caption>
            <CustomInput
              size="small"
              id="custom-field-placeholder"
              value={form.placeholder}
              onChange={(e: any) => set("placeholder", e.target.value)}
              placeHolder="Enter placeholder text"
              propsToInputElement={{
                "aria-label": "Placeholder",
                maxLength: TEXT_LIMITS.placeholder,
              }}
            />
            <Typography variant="caption" sx={helperSx}>
              Shown until the signer fills the field.
            </Typography>
          </Box>
        )}

        <Box>
          <Caption>Tooltip</Caption>
          <CustomInput
            size="small"
            id="custom-field-tooltip"
            value={form.toolTip}
            onChange={(e: any) => set("toolTip", e.target.value)}
            placeHolder="Enter tooltip text"
            propsToInputElement={{
              "aria-label": "Tooltip",
              maxLength: TEXT_LIMITS.toolTip,
            }}
          />
          <Typography variant="caption" sx={helperSx}>
            Extra help the signer sees on hover.
          </Typography>
        </Box>

        {isDate && (
          <Box>
            <Caption id="custom-field-date-format-label">Date Format</Caption>
            <Select
              fullWidth
              size="small"
              labelId="custom-field-date-format-label"
              value={form.dateFormat}
              onChange={(e) => set("dateFormat", e.target.value as string)}
              sx={selectSx}
            >
              {DATE_FORMAT_OPTIONS.map((fmt) => (
                <MenuItem key={fmt} value={fmt} sx={{ fontSize: "0.875rem" }}>
                  {fmt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {isDropDown && (
          <Box data-field>
            <Caption required>Options</Caption>
            <Stack spacing={1}>
              {form.listItems.map((item, index) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                >
                  <CustomInput
                    size="small"
                    id={`custom-field-option-${index}`}
                    value={item.text}
                    onChange={(e: any) => renameOption(index, e.target.value)}
                    onBlur={() => touch("listItems")}
                    placeHolder={`Option ${index + 1}`}
                    propsToInputElement={{
                      "aria-label": `Option ${index + 1}`,
                      maxLength: TEXT_LIMITS.option,
                    }}
                  />
                  <Tooltip title="Remove option" arrow describeChild>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Remove option ${index + 1}`}
                        disabled={form.listItems.length <= 1}
                        onClick={() => removeOption(index)}
                        sx={{ color: "text.secondary" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
            {isTruthy(showError("options")) ? (
              <FormHelperText error role="alert">
                {showError("options")}
              </FormHelperText>
            ) : (
              <Typography variant="caption" sx={helperSx}>
                What the signer can choose from.
              </Typography>
            )}
            <Button
              size="small"
              variant="text"
              onClick={addOption}
              startIcon={<PlusIcon size={14} />}
              sx={{ mt: 1, textTransform: "none", color: "primary.main" }}
            >
              Add option
            </Button>
          </Box>
        )}

        {(isText || isDropDown) && (
          <Box data-field>
            <Caption id="custom-field-default-label">Default Value</Caption>
            {isDropDown ? (
              <Select
                fullWidth
                size="small"
                labelId="custom-field-default-label"
                displayEmpty
                value={form.defaultValue}
                onChange={(e) =>
                  setAndTouch("defaultValue", e.target.value as string)
                }
                sx={selectSx}
              >
                <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                  No default
                </MenuItem>
                {form.listItems
                  .filter((item) => item.text.trim() !== "")
                  .map((item) => (
                    <MenuItem
                      key={item.id}
                      value={item.text.trim()}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {item.text.trim()}
                    </MenuItem>
                  ))}
              </Select>
            ) : (
              <CustomInput
                size="small"
                id="custom-field-default"
                value={form.defaultValue}
                onChange={(e: any) => set("defaultValue", e.target.value)}
                onBlur={() => touch("defaultValue")}
                placeHolder="Pre-filled for the signer"
                error={showError("defaultValue")}
                propsToInputElement={{
                  "aria-label": "Default value",
                  // Never longer than the field itself may be.
                  maxLength: MAX_FIELD_LENGTH,
                }}
              />
            )}
            {isTruthy(showError("defaultValue")) ? (
              <FormHelperText error role="alert">
                {showError("defaultValue")}
              </FormHelperText>
            ) : (
              <Typography variant="caption" sx={helperSx}>
                The signer can still change it.
              </Typography>
            )}
          </Box>
        )}

        <Stack spacing={1.5}>
          {switchRow(
            "Required",
            "required",
            "The signer cannot finish without filling this.",
          )}
          {switchRow(
            "Read Only",
            "readOnly",
            "The signer can see the value but not change it.",
          )}
          {isText &&
            switchRow(
              "Multiline",
              "multiLine",
              "Allows more than one line of text.",
            )}
        </Stack>

        {isText && (
          <>
            <Divider />

            <Typography variant="caption" sx={sectionTitleSx}>
              Validations
            </Typography>

            <Box>
              <Caption id="custom-field-validation-label">
                Validation Type
              </Caption>
              <Select
                fullWidth
                size="small"
                labelId="custom-field-validation-label"
                value={
                  form.customValidation
                    ? CUSTOM_PATTERN_VALUE
                    : form.validationPattern || "none"
                }
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === CUSTOM_PATTERN_VALUE) {
                    set("customValidation", true);
                    // Left untouched: a blank pattern box isn't an error until
                    // the sender has had a chance to type into it.
                    set("validationPattern", "");
                  } else {
                    set("customValidation", false);
                    setAndTouch("validationPattern", value);
                    if (value === "none") set("validationMessage", "");
                  }
                }}
                sx={selectSx}
              >
                {Object.keys(signerFieldValidationPatterns).map((pattern) => (
                  <MenuItem
                    key={pattern}
                    value={pattern}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {pattern}
                  </MenuItem>
                ))}
                <MenuItem
                  value={CUSTOM_PATTERN_VALUE}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Custom pattern
                </MenuItem>
              </Select>
              <Typography variant="caption" sx={helperSx}>
                What the signer is allowed to type.
              </Typography>
            </Box>

            {form.customValidation && (
              <Box data-field>
                <Caption required>Pattern</Caption>
                <CustomInput
                  size="small"
                  id="custom-field-pattern"
                  value={form.validationPattern}
                  onChange={(e: any) => set("validationPattern", e.target.value)}
                  onBlur={() => touch("validationPattern")}
                  placeHolder="e.g. ^[A-Z]{2}-[0-9]{4}$"
                  error={showError("pattern")}
                  helperText={showError("pattern")}
                  propsToInputElement={{
                    "aria-label": "Validation pattern",
                    maxLength: TEXT_LIMITS.pattern,
                  }}
                />
                {!showError("pattern") && (
                  <Typography variant="caption" sx={helperSx}>
                    A regular expression the value must match.
                  </Typography>
                )}
              </Box>
            )}

            {(form.customValidation || form.validationPattern !== "none") && (
              <Box data-field>
                <Caption required={form.customValidation}>
                  Validation Message
                </Caption>
                <CustomInput
                  size="small"
                  id="custom-field-validation-message"
                  value={form.validationMessage}
                  onChange={(e: any) => set("validationMessage", e.target.value)}
                  onBlur={() => touch("validationMessage")}
                  placeHolder="e.g. Use 2 letters, a dash, then 4 digits"
                  error={showError("validationMessage")}
                  helperText={showError("validationMessage")}
                  propsToInputElement={{
                    "aria-label": "Validation message",
                    maxLength: TEXT_LIMITS.validationMessage,
                  }}
                />
                {!showError("validationMessage") && (
                  <Typography variant="caption" sx={helperSx}>
                    Shown to the signer when the value does not match.
                  </Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }} data-field>
                <Caption>Min Length</Caption>
                <CustomInput
                  size="small"
                  type="number"
                  id="custom-field-min-length"
                  value={form.minLength || ""}
                  onChange={(e: any) => setAndTouch("minLength", lengthInput(e))}
                  onKeyDown={blockNonLengthKeys}
                  placeHolder="0"
                  error={showError("minLength")}
                  helperText={showError("minLength")}
                  propsToInputElement={{
                    "aria-label": "Min length",
                    min: 0,
                    max: MAX_FIELD_LENGTH,
                    step: 1,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }} data-field>
                <Caption required>Max Length</Caption>
                <CustomInput
                  size="small"
                  type="number"
                  id="custom-field-max-length"
                  value={form.maxLength || ""}
                  onChange={(e: any) => setAndTouch("maxLength", lengthInput(e))}
                  onKeyDown={blockNonLengthKeys}
                  placeHolder="200"
                  error={showError("maxLength")}
                  helperText={showError("maxLength")}
                  propsToInputElement={{
                    "aria-label": "Max length",
                    min: 1,
                    max: MAX_FIELD_LENGTH,
                    step: 1,
                  }}
                />
              </Box>
            </Stack>
          </>
        )}

        {/* No length section for a dropdown: the value is always one of the options above, so a
            length rule could only refuse a choice the signer had no way to avoid. */}
      </Stack>
    </AppModal>
  );
};

export default CustomFieldModal;
